import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { useTheme } from 'contexts/ThemeContext'
import { interviewAudioApi } from 'lib/api'
import { Mail, Key, AlertCircle, ArrowRight } from 'lucide-react'

export function InterviewLoginPage() {
    const { t, i18n } = useTranslation()
    const { theme } = useTheme()
    const [email, setEmail] = useState('')
    const [accessCode, setAccessCode] = useState('')
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const logoSrc = theme === 'dark' ? '/logodark512.png' : '/logo512.png'

    // Handle auto-login if URL parameters are present
    React.useEffect(() => {
        const emailParam = searchParams.get('email')
        const accessCodeParam = searchParams.get('access_code')

        if (emailParam && accessCodeParam) {
            setEmail(emailParam)
            setAccessCode(accessCodeParam)

            // Trigger login
            handleLogin(emailParam, accessCodeParam)
        }
    }, [searchParams])

    const handleLogin = async (loginEmail: string, loginCode: string) => {
        setError('')
        setIsLoading(true)

        try {
            const response = await interviewAudioApi.login(loginEmail, loginCode)
            const {
                interview_id,
                candidate_name,
                candidate_photo,
                offer_title,
                questions,
                status,
                is_deadline_passed,
                language,
                current_question_index,
                consent_given,
                consent_timestamp
            } = response.data;

            if (language) {
                i18n.changeLanguage(language)
            }

            // Store for session recovery on reload
            localStorage.setItem('interview_id', interview_id.toString())
            localStorage.setItem('interview_access_code', loginCode)

            const slug = candidate_name.replace(/\s+/g, '_').toLowerCase()
            navigate(`/interview_audio/${slug}`, {
                state: {
                    interview_id,
                    candidateName: candidate_name,
                    candidatePhoto: candidate_photo,
                    offerTitle: offer_title,
                    questions,
                    status,
                    is_deadline_passed,
                    language,
                    currentQuestionIndex: current_question_index,
                    consentGiven: consent_given,
                    consentTimestamp: consent_timestamp
                }
            })
        } catch (error: any) {
            setError(error.response?.data?.error || t('interview.login.error'))
        } finally {
            setIsLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        handleLogin(email, accessCode)
    }

    return (
        <div className="min-h-screen bg-muted flex items-center justify-center p-4" translate="no">
            <div className="w-full max-w-md bg-card rounded-xl shadow-lg border p-8 relative">
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <img src={logoSrc} alt="Selektia" className="h-16 w-auto" />
                    </div>
                    <h1 className="text-2xl font-bold">{t('interview.login.title')}</h1>
                    <p className="text-muted-foreground mt-2">
                        {t('interview.login.subtitle')}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <div className="flex items-center space-x-2 p-3 bg-destructive/10 rounded-md text-destructive text-sm">
                            <AlertCircle className="h-4 w-4" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">{t('interview.login.emailLabel')}</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="email"
                                type="email"
                                placeholder={t('interview.login.emailPlaceholder')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="pl-10 h-11"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="accessCode" className="text-sm font-medium">{t('interview.login.accessCodeLabel')}</label>
                        <div className="relative">
                            <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                id="accessCode"
                                type="text"
                                placeholder={t('interview.login.accessCodePlaceholder')}
                                value={accessCode}
                                onChange={(e) => setAccessCode(e.target.value)}
                                className="pl-10 h-11 font-mono"
                                required
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full h-11 mt-6"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center space-x-2">
                                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                <span>{t('interview.login.verifying')}</span>
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                {t('interview.login.accessButton')} <ArrowRight className="h-4 w-4" />
                            </span>
                        )}
                    </Button>
                </form>

                <div className="mt-6 text-center text-xs text-muted-foreground">
                    {t('interview.login.helpText')}
                </div>
            </div>
        </div>
    )
}
